<template>
  <div>
    <div class="p-relative header-image pb-5"></div>

    <b-container fluid class="custom-container mt-4 mt-md-5 px-3 px-md-4">
      <!-- Page heading -->
      <h4 class="font-weight-bold pt-4 mb-4">Files</h4>

      <!-- The page's own shape, with grey placeholders, until the first load is
           done, so the empty states ("No folders yet", zero counts) never flash
           before real data. -->
      <files-loading-skeleton v-if="!ready" :can-upload="canUpload" />

      <!-- Main layout -->
      <b-row v-else class="files-row">
        <b-col cols="12" lg="3" class="pr-lg-4 mb-3 mb-lg-0">
          <!-- Below lg the sidebar folds away behind a button naming the current
               view, so the files come first instead of a long list. -->
          <button
            v-if="!isDesktop"
            type="button"
            class="view-switcher"
            :aria-expanded="viewsOpen ? 'true' : 'false'"
            data-test="view-switcher"
            @click="viewsOpen = !viewsOpen"
          >
            <span class="view-switcher-text">
              <span class="view-switcher-label" data-test="view-switcher-label">
                {{ currentViewLabel }}
              </span>
              <span class="view-switcher-hint">Folders and projects</span>
            </span>
            <span v-if="currentViewCount !== null" class="view-switcher-count">
              {{ currentViewCount }}
            </span>
            <b-icon
              :icon="viewsOpen ? 'chevron-up' : 'chevron-down'"
              font-scale="0.9"
              aria-hidden="true"
            />
          </button>

          <b-collapse :visible="isDesktop || viewsOpen" data-test="views-panel">
            <file-sidebar
              :class="{ 'mt-2': !isDesktop }"
              :selected-folder="selectedFolder"
              :sections="sections"
              :projects="sidebarProjects"
              :can-manage-folders="canManageFolders"
              @folder-change="onFolderChange"
              @new-folder="openCreateFolder"
              @suggest-folders="suggestFolders"
              @rename-folder="openRenameFolder"
              @delete-folder="deleteFolder"
              @download-folder="downloadFolder"
              @drop-files="dropFiles"
            />
          </b-collapse>
        </b-col>

        <b-col cols="12" lg="9">
          <b-card class="files-card" body-class="p-3 p-md-4">
            <div v-if="isProjectView" data-test="project-view">
              <button
                type="button"
                class="crumb-link"
                data-test="project-back"
                @click="onFolderChange('projects')"
              >
                <b-icon icon="chevron-left" font-scale="0.8" />
                Projects
              </button>
              <div class="project-view-head">
                <div>
                  <h4 class="project-view-title">{{ selectedProject.name }}</h4>
                  <p v-if="selectedProject.read_only" class="project-view-note">
                    {{ sharedByText }}
                  </p>
                </div>
                <router-link
                  :to="projectLink"
                  class="project-view-link"
                  data-test="project-open"
                >
                  Open project
                </router-link>
              </div>
              <project-files-panel
                class="mt-3"
                :project-id="selectedProject.id"
                embedded
              />
            </div>
            <projects-overview
              v-else-if="isProjectsOverview"
              :projects="sidebarProjects"
              @open="onFolderChange"
            />
            <file-content
              v-else
              :selected-folder="selectedFolder"
              :selected-type="selectedType"
              :search="search"
              :can-upload="canUpload"
              :folders="folders"
              :can-manage-folders="canManageFolders"
              :current-folder-label="currentFolderLabel"
              :files="rows"
              :files-meta="filesMeta"
              :current-page="currentPage"
              :loading="loading"
              :empty-message="emptyMessage"
              :type-counts="typeCounts"
              :sort-by="tableSort.by"
              :sort-desc="tableSort.desc"
              :load-all-rows="loadAllRows"
              @folder-change="onFolderChange"
              @type-change="onTypeChange"
              @search-change="onSearchChange"
              @page-change="onPageChange"
              @new-folder="openCreateFolder"
              @suggest-folders="suggestFolders"
              @set-folders="setFolders"
              @open-file="markRead"
              @upload="createNewFile"
              @download-file="downloadFile"
              @share-file="openShare"
              @edit-file="editFile"
              @delete-file="deleteFile"
              @sort-change="onSortChange"
              @bulk-download="bulkDownload"
              @bulk-share="bulkShare"
              @bulk-delete="bulkDelete"
              @bulk-folder="bulkFolder"
            />
          </b-card>
        </b-col>
      </b-row>
    </b-container>

    <share-file-modal
      :show-share-files="share.visible"
      :selected-files="share.ids"
      :file-shares="share.fileShares"
      :is-reshare="selectedFolder === 'received'"
      @closeShareFileModal="share.visible = false"
      @fileShareResponsePrompt="onShareResponse"
      @reloadFileList="reloadAfterShare"
    />

    <folder-name-modal
      :visible="folderModal.visible"
      :title="folderModal.mode === 'create' ? 'New folder' : 'Edit folder'"
      :initial-name="folderModal.name"
      :initial-color="folderModal.color"
      :initial-parent-id="folderModal.parentId"
      :parent-options="folderParentOptions"
      :save-label="folderModal.mode === 'create' ? 'CREATE' : 'SAVE'"
      :saving="folderModal.saving"
      :error="folderModal.error"
      @save="saveFolder"
      @close="closeFolderModal"
    />
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { mapGetters } from 'vuex'
import Api from '@/api'
import FileContent from '@/components/files/FileContent'
import FileSidebar from '@/components/files/FileSidebar'
import FilesLoadingSkeleton from '@/components/files/FilesLoadingSkeleton'
import ProjectsOverview from '@/components/files/ProjectsOverview'
import FolderNameModal from '@/components/files/FolderNameModal'
import ProjectFilesPanel from '@/components/projects/ProjectFilesPanel'
import ShareFileModal from '@/components/modals/ShareFileModal'
import File from '@/models/File'
import Folder from '@/models/Folder'
import { buildFolderTree, parentChoices } from '@/utils/folderTree'
import { parseServerDate } from '@/utils/dateHelper'
import { zipDownloadName } from '@/utils/zipDownloadName'
import { expiryDateOf, expiryState } from '@/utils/fileExpiry'
import { fileKind } from '@/utils/fileTypeIcons'
import viewport from '@/mixins/viewport'
import {
  getOwnedByLabel,
  getCreatedByLabel,
  isSameCreatorAndOwner,
  buildCreatorNameResolverFromFiles,
} from '@/utils/fileLabels'

export default {
  mixins: [viewport],

  components: {
    FileContent,
    FilesLoadingSkeleton,
    FileSidebar,
    ProjectsOverview,
    FolderNameModal,
    ProjectFilesPanel,
    ShareFileModal,
  },

  data() {
    const q = this.$route.query
    return {
      // The sidebar's panel on screens below lg (always open above it).
      viewsOpen: false,
      files: [],
      filesMeta: {},
      // False until the first load has finished (see the template).
      ready: false,
      folderList: [],
      projectList: [],
      counts: { all: 0, recent: 0, expiring: 0, received: 0, sent: 0 },
      share: { visible: false, ids: [], fileShares: [] },
      loading: false,
      latestRequestId: 0,
      currentPage: Number(q.page) || 1,
      selectedFolder: q.folder || 'all',
      selectedType: q.type || 'all',
      search: q.search || '',
      sort: q.sort || '',
      folderModal: {
        visible: false,
        mode: 'create',
        folder: null,
        name: '',
        color: null,
        saving: false,
        error: '',
      },
    }
  },

  computed: {
    ...mapGetters('auth', ['user', 'hasPermission']),

    isBusinessAdmin() {
      return !!this.user && this.user.user_type === 'business_admin'
    },

    // The API checks this too; here it only keeps controls that would fail out of sight.
    canManageFolders() {
      return !this.isBusinessAdmin || this.hasPermission('files.edit')
    },

    // Tradies, or business admins with files.upload.
    canUpload() {
      if (!this.user) return false
      if (this.isBusinessAdmin) return this.hasPermission('files.upload')
      return this.user.user_type === 'tradie'
    },

    sections() {
      return [
        {
          id: 'general',
          items: [
            { id: 'all', label: 'All files', count: this.counts.all },
            { id: 'recent', label: 'Recent', count: this.counts.recent },
            {
              id: 'expiring',
              label: 'Expiring',
              count: this.counts.expiring,
            },
          ],
        },
        {
          id: 'sharing',
          items: [
            { id: 'received', label: 'Received', count: this.counts.received },
            { id: 'sent', label: 'Sent', count: this.counts.sent },
          ],
        },
        {
          id: 'folders',
          items: buildFolderTree(this.folderList),
        },
      ]
    },

    folders() {
      const section = this.sections.find((section) => section.id === 'folders')
      return section ? section.items : []
    },

    // Where the folder in the dialog can go: not inside itself, and not past the depth limit.
    folderParentOptions() {
      // Made from inside a folder, it goes in that folder: nothing to choose.
      if (this.folderModal.lockParent) return []
      return parentChoices(this.folders, this.folderModal.folder)
    },

    isShareView() {
      return ['received', 'sent'].includes(this.selectedFolder)
    },

    rows() {
      return this.toRows(this.files)
    },

    typeCounts() {
      return this.isShareView ? null : this.counts.types || null
    },

    // The API sorts the listing; with no choice it is newest first.
    tableSort() {
      const desc = this.sort.charAt(0) === '-'
      const key = this.sort.replace(/^-/, '')
      if (key === 'name') return { by: 'display_name', desc }
      if (key === 'created_at') return { by: 'added', desc }
      return { by: 'added', desc: true }
    },

    emptyMessage() {
      if (this.selectedFolder === 'received' && !this.search) {
        return 'No files have been shared with you yet.'
      }
      if (this.selectedFolder === 'sent' && !this.search) {
        return 'You have not shared any files yet.'
      }
      if (this.selectedFolder === 'expiring' && !this.search) {
        return 'Nothing is expiring or expired.'
      }
      if (this.search || this.selectedType !== 'all') {
        return 'No files match your search or filter.'
      }
      if (/^\d+$/.test(this.selectedFolder)) {
        return 'No files in this folder yet. Use the ⋯ menu on a file to move it here.'
      }
      return 'There are no files to show.'
    },

    queryState() {
      const query = {}
      if (this.selectedType !== 'all') query.type = this.selectedType
      if (this.selectedFolder !== 'all') query.folder = this.selectedFolder
      if (this.search) query.search = this.search
      if (this.sort) query.sort = this.sort
      if (this.currentPage > 1) query.page = String(this.currentPage)
      return query
    },

    canSeeProjectFiles() {
      return (
        !!this.user &&
        (this.isBusinessAdmin ||
          (this.user.user_type === 'tradie' && !!this.user.is_supervisor))
      )
    },

    // Projects use the "project:<id>" folder id so they share the folder URL param;
    // "projects" is the list of them that the sidebar's Projects tab opens.
    isProjectView() {
      return !!this.selectedProject
    },

    isProjectsOverview() {
      return this.selectedFolder === 'projects' && this.projectList.length > 0
    },

    selectedProject() {
      if (!/^project:\d+$/.test(this.selectedFolder)) return null
      const id = Number(this.selectedFolder.slice('project:'.length))
      return this.projectList.find((project) => project.id === id) || null
    },

    sidebarProjects() {
      return this.projectList.map((project) => ({
        id: `project:${project.id}`,
        label: project.name,
        count: project.files_count,
        readOnly: project.read_only,
        archived: !!project.is_archived,
        sharedBy: project.shared_by,
      }))
    },

    sharedByText() {
      const company = this.selectedProject && this.selectedProject.shared_by
      return `Shared by ${company || 'another company'}`
    },

    projectLink() {
      const project = this.selectedProject
      return project.read_only && project.share_id
        ? {
            name: 'projects.shared-view-page',
            params: { shareId: String(project.share_id) },
            query: { tab: 'files' },
          }
        : {
            name: 'projects.view-page',
            params: { id: String(project.id) },
            query: { tab: 'files' },
          }
    },

    // The view shown on the switcher button below lg.
    currentViewLabel() {
      if (this.isProjectsOverview) return 'Projects'
      if (this.selectedProject) return this.selectedProject.name
      return this.currentFolderLabel || 'All files'
    },

    currentViewCount() {
      if (this.isProjectsOverview) return this.projectList.length
      if (this.selectedProject) return this.selectedProject.files_count
      const items = this.sections.reduce((all, s) => all.concat(s.items), [])
      const match = items.find((item) => item.id === this.selectedFolder)
      return match && typeof match.count === 'number' ? match.count : null
    },

    currentFolderLabel() {
      const items = this.sections.reduce((all, s) => all.concat(s.items), [])
      const match = items.find((item) => item.id === this.selectedFolder)
      return match ? match.label : ''
    },
  },

  methods: {
    // The projects list or one project: the company file list is not needed there.
    onProjectSide(folderId) {
      return folderId === 'projects' || /^project:/.test(folderId)
    },

    onFolderChange(folderId) {
      this.selectedFolder = folderId
      this.currentPage = 1
      this.viewsOpen = false
    },

    onTypeChange(typeId) {
      this.selectedType = typeId
      this.currentPage = 1
    },

    onSearchChange(value) {
      this.search = value
      this.currentPage = 1
    },

    onPageChange(value) {
      this.currentPage = value
    },

    onSortChange({ sortBy, sortDesc }) {
      if (this.isShareView) return
      const key = { display_name: 'name', added: 'created_at' }[sortBy]
      if (!key) return
      this.sort = `${sortDesc ? '-' : ''}${key}`
      this.currentPage = 1
    },

    addedLabel(value) {
      if (!value) return '-'
      const days = Math.max(
        dayjs().startOf('day').diff(dayjs(value).startOf('day'), 'day'),
        0,
      )
      if (days === 0) return 'Today'
      if (days <= 14) return `${days} ${days === 1 ? 'day' : 'days'} ago`
      return `${Math.floor(days / 7)} weeks ago`
    },

    toast(message, variant = 'success') {
      this.$bvToast.toast(message, { variant, solid: true })
    },

    async loadCounts() {
      const params = {}
      if (this.search) params.search = this.search
      if (/^(\d+|recent|expiring)$/.test(this.selectedFolder)) {
        params.folder = this.selectedFolder
      }
      try {
        const res = await Api.get('file-counts', { params })
        this.counts = res.data.data
      } catch (error) {
        console.error('error', error)
      }
    },

    createNewFile() {
      if (!this.canUpload) return
      this.$router.push({ name: 'templates.list-page' })
    },

    async editFile(file) {
      await this.$store.dispatch('file/getFile', file.id)
      this.$router.push({ name: 'file.edit-page', params: { id: file.id } })
    },

    // Shared files are kept a week so the company can review them (see FileOptions).
    async deleteFile(file) {
      const companies = file.shared_with_companies || []
      const body = companies.length
        ? `This file has been shared with ${companies
            .map((company) => company.name)
            .join(
              ', ',
            )}. If you delete it, the company will be notified and given 1 week to review the file.`
        : 'Are you sure you want to delete the selected file?'
      const confirmed = await this.$bvModal.msgBoxConfirm(body, {
        title: companies.length ? 'Delete this file?' : 'Delete selected file?',
        okTitle: 'DELETE FILE',
        cancelTitle: 'CANCEL',
        okVariant: 'primary',
        cancelVariant: 'outline-secondary',
        centered: true,
      })
      if (!confirmed) return

      try {
        const res = await Api.delete(`file/${file.id}`)
        const scheduled = res.data.data && res.data.data.pending_deletion
        await Promise.all([
          this.loadFiles(),
          this.loadCounts(),
          this.loadFolders(),
        ])
        this.toast(scheduled ? 'File scheduled for deletion.' : 'File deleted.')
      } catch (error) {
        this.toast('Could not delete the file.', 'danger')
      }
    },

    openShare(file) {
      this.share = {
        visible: true,
        ids: [file.id],
        fileShares: file.file_shares || [],
      }
    },

    reloadAfterShare() {
      this.loadFiles()
      this.loadCounts()
    },

    onShareResponse(res) {
      if (res.success) {
        this.reloadAfterShare()
        this.toast('File shared successfully.')
        return
      }
      this.toast(
        res.message || 'Something went wrong while sharing the file.',
        'danger',
      )
    },

    async downloadFile(file) {
      try {
        const res = await Api.get(`download-file/${file.id}`, {
          responseType: 'blob',
        })
        const owner =
          (file.user && (file.user.full_name || file.user.email)) || 'file'
        this.saveZip(res.data, zipDownloadName(`${owner} - ${file.name}`))
      } catch (error) {
        this.toast(
          await this.downloadErrorMessage(
            error,
            'Could not download the file.',
          ),
          'danger',
        )
      }
    },

    // A blob download hides the API's JSON error body inside a Blob.
    async downloadErrorMessage(error, fallback) {
      const body = error && error.response && error.response.data
      if (!(body instanceof Blob)) return fallback
      try {
        const text = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsText(body)
        })
        return JSON.parse(text).message || fallback
      } catch (e) {
        return fallback
      }
    },

    saveZip(data, filename) {
      const url = URL.createObjectURL(
        new Blob([data], { type: 'application/zip' }),
      )
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },

    async downloadMany(ids, filename) {
      try {
        const res = await Api.get('download-bulk-files', {
          params: { files: ids },
          responseType: 'blob',
        })
        this.saveZip(res.data, filename)
      } catch (error) {
        this.toast(
          await this.downloadErrorMessage(
            error,
            'Could not download the files.',
          ),
          'danger',
        )
      }
    },

    bulkDownload(files) {
      return this.downloadMany(
        files.map((file) => file.id),
        'Files.zip',
      )
    },

    async downloadFolder(folder) {
      try {
        const res = await Api.get('file', {
          params: { 'filter[folder]': folder.id, all: 1 },
        })
        const ids = res.data.data.map((file) => file.id)
        if (!ids.length) {
          this.toast(`"${folder.label}" has no files to download.`, 'danger')
          return
        }
        await this.downloadMany(ids, zipDownloadName(folder.label))
      } catch (error) {
        this.toast('Could not download the folder.', 'danger')
      }
    },

    bulkShare(files) {
      this.share = {
        visible: true,
        ids: files.map((file) => file.id),
        fileShares: [],
      }
    },

    async bulkDelete(files) {
      const shared = files.some(
        (file) => (file.shared_with_companies || []).length,
      )
      const body = shared
        ? 'Some of these files have been shared with a company. If you delete them, the company will be notified and given 1 week to review those files.'
        : `Are you sure you want to delete the ${files.length} selected files?`
      const confirmed = await this.$bvModal.msgBoxConfirm(body, {
        title: `Delete ${files.length} files?`,
        okTitle: 'DELETE FILES',
        cancelTitle: 'CANCEL',
        okVariant: 'primary',
        cancelVariant: 'outline-secondary',
        centered: true,
      })
      if (!confirmed) return

      const results = await Promise.all(
        files.map((file) =>
          Api.delete(`file/${file.id}`).then(
            () => true,
            () => false,
          ),
        ),
      )
      await Promise.all([
        this.loadFiles(),
        this.loadCounts(),
        this.loadFolders(),
      ])
      this.reportBulk(results, 'deleted')
    },

    reportBulk(results, verb) {
      const done = results.filter(Boolean).length
      if (done === results.length) {
        this.toast(`${done} ${done === 1 ? 'file' : 'files'} ${verb}.`)
      } else {
        this.toast(
          `${done} of ${results.length} files ${verb}. The rest failed.`,
          'danger',
        )
      }
    },

    bulkFolder({ files, folderId, add }) {
      return this.applyFolder(
        files.map((file) => file.id),
        folderId,
        add,
      )
    },

    dropFiles({ folderId, ids }) {
      return this.applyFolder(ids, folderId, true)
    },

    // Adds files to (or takes them out of) one folder; their other folders stay as they are.
    async applyFolder(ids, folderId, add) {
      if (!this.canManageFolders) return
      const folder = Number(folderId)
      const match = this.folders.find((item) => item.id === String(folderId))
      const label = match ? match.label : 'that folder'
      const targets = ids
        .map((id) => this.rowCache[id])
        .filter(
          (row) => row && add !== row.folder_ids.map(Number).includes(folder),
        )
      if (!targets.length) {
        this.toast(
          add
            ? `Those files are already in ${label}.`
            : `Those files are not in ${label}.`,
          'danger',
        )
        return
      }

      const results = await Promise.all(
        targets.map((row) => {
          const current = row.folder_ids.map(Number)
          const next = add
            ? current.concat(folder)
            : current.filter((id) => id !== folder)
          return Api.put(`file/${row.id}/folders`, { folder_ids: next }).then(
            () => true,
            () => false,
          )
        }),
      )
      await Promise.all([this.loadFolders(), this.loadFiles()])
      this.reportBulk(
        results,
        add ? `added to ${label}` : `removed from ${label}`,
      )
    },

    markRead(fileId) {
      Api.put(`file-receives/read/${fileId}`)
        .then(() => this.loadCounts())
        .catch(() => {})
    },

    toRows(files) {
      // Received / Sent rows are shares wrapping the file.
      const resolveName = buildCreatorNameResolverFromFiles(
        files.map((f) => f.file || f),
      )
      return files.map((f) => {
        const file = f.file || f
        const owner = getOwnedByLabel(f)
        const addedAt = parseServerDate(f.created_at)
        return {
          id: file.id,
          display_name: file.display_name,
          folder_ids: file.folder_ids || [],
          file,
          kind: fileKind(file),
          owner,
          created_by:
            this.selectedFolder === 'sent'
              ? (f.shared_to && f.shared_to.full_name) || '-'
              : isSameCreatorAndOwner(file, resolveName)
              ? owner
              : getCreatedByLabel(file, resolveName),
          is_new: this.selectedFolder === 'received' && !f.read_at,
          expiry: expiryState(file),
          expiry_date: expiryDateOf(file),
          added: this.addedLabel(addedAt),
          added_at: addedAt,
        }
      })
    },

    // One page of the current list (any folder, filter or search).
    async fetchListPage(page, perPage) {
      if (this.isShareView) {
        const params = { page, per_page: perPage }
        if (this.search) params['filter[search]'] = this.search
        if (this.selectedType !== 'all')
          params['filter[type]'] = this.selectedType
        const endpoint =
          this.selectedFolder === 'received' ? 'file-receives' : 'file-shares'
        const res = await Api.get(endpoint, { params })
        return { data: res.data.data, meta: res.data.meta }
      }
      const query = new File()
        .page(page)
        .params({ per_page: perPage, ...(this.sort && { sort: this.sort }) })
        .where('search', this.search)
      if (this.selectedFolder === 'recent') {
        query.where('recent', 1)
      }
      if (this.selectedFolder === 'expiring') {
        query.where('status', 'expiring soon,expired')
      }
      if (this.selectedType !== 'all') {
        query.where('type', this.selectedType)
      }
      if (/^\d+$/.test(this.selectedFolder)) {
        query.where('folder', this.selectedFolder)
      }
      const res = await query.get()
      return { data: res.data, meta: res.meta }
    },

    // Every file in the current list up to the limit, for "select all N files".
    async loadAllRows(limit) {
      const all = []
      let page = 1
      let lastPage = 1
      do {
        const { data, meta } = await this.fetchListPage(page, 100)
        all.push(...data)
        lastPage = (meta && meta.last_page) || 1
        page += 1
      } while (page <= lastPage && all.length < limit)
      const rows = this.toRows(all.slice(0, limit))
      // Remember them like any shown page, so a bulk folder change can reach them.
      rows.forEach((row) => {
        this.rowCache[row.id] = row
      })
      return rows
    },

    async loadFiles() {
      // Only the latest request may write results, so a slow older one can't overwrite them.
      const requestId = ++this.latestRequestId
      this.loading = true
      try {
        const { data, meta } = await this.fetchListPage(this.currentPage, 15)
        if (requestId !== this.latestRequestId) return
        this.files = data
        this.filesMeta = meta
      } catch (error) {
        console.error('error', error)
      } finally {
        if (requestId === this.latestRequestId) this.loading = false
      }
    },

    async loadProjects() {
      if (!this.canSeeProjectFiles) return
      try {
        const res = await Api.get('projects/files-summary')
        this.projectList = Array.isArray(res.data.data) ? res.data.data : []
      } catch (error) {
        this.projectList = []
      }
      // A link to a project that isn't there (or isn't allowed) lands on All files.
      const missingProject =
        /^project:/.test(this.selectedFolder) && !this.selectedProject
      const noProjects =
        this.selectedFolder === 'projects' && !this.projectList.length
      if (missingProject || noProjects) this.onFolderChange('all')
    },

    async loadFolders() {
      try {
        const res = await new Folder().get()
        this.folderList = res.data
      } catch (error) {
        console.error('error', error)
      }
    },

    // `parent` is the folder it opens inside (from a folder's "New sub-folder"), if any.
    openCreateFolder(parent) {
      if (!this.canManageFolders) return
      this.folderModal = {
        visible: true,
        mode: 'create',
        folder: null,
        name: '',
        color: null,
        parentId: parent && parent.id ? parent.id : null,
        lockParent: !!(parent && parent.id),
        saving: false,
        error: '',
      }
    },

    openRenameFolder(folder) {
      if (!this.canManageFolders) return
      this.folderModal = {
        visible: true,
        mode: 'rename',
        folder,
        name: folder.label,
        color: folder.color || null,
        parentId: folder.parentId || null,
        saving: false,
        error: '',
      }
    },

    closeFolderModal() {
      this.folderModal.visible = false
    },

    nameError(error) {
      const data = error.response && error.response.data
      const errors = (data && data.errors) || {}
      const messages = errors.name || errors.parent_id
      return messages && messages.length
        ? messages[0]
        : 'Could not save the folder. Please try again.'
    },

    // `parentId` is left out (not just null) to keep the folder where it is.
    async saveFolder(name, color = null, parentId) {
      const modal = this.folderModal
      modal.saving = true
      modal.error = ''
      try {
        if (modal.mode === 'create') {
          const res = await Api.post('folders', {
            name,
            color,
            ...(parentId === undefined ? {} : { parent_id: parentId }),
          })
          await this.loadFolders()
          this.onFolderChange(String(res.data.data.id))
          this.toast('Folder created.')
        } else {
          await Api.put(`folders/${modal.folder.id}`, {
            name,
            color,
            ...(parentId === undefined ? {} : { parent_id: parentId }),
          })
          await this.loadFolders()
          this.toast('Folder saved.')
        }
        modal.visible = false
      } catch (error) {
        modal.error = this.nameError(error)
      } finally {
        modal.saving = false
      }
    },

    async suggestFolders() {
      if (!this.canManageFolders) return
      try {
        await Api.post('folders/suggested')
        await this.loadFolders()
        this.toast('Suggested folders added. You can rename or delete them.')
      } catch (error) {
        this.toast('Could not add the suggested folders.', 'danger')
      }
    },

    async deleteFolder(folder) {
      if (!this.canManageFolders) return
      const confirmed = await this.$bvModal.msgBoxConfirm(
        "The files in this folder won't be deleted. They move back to All files, and any folders inside it move up a level.",
        {
          title: `Delete "${folder.label}"?`,
          okTitle: 'DELETE FOLDER',
          cancelTitle: 'CANCEL',
          okVariant: 'primary',
          cancelVariant: 'outline-secondary',
          centered: true,
        },
      )
      if (!confirmed) return

      try {
        await Api.delete(`folders/${folder.id}`)
        if (this.selectedFolder === folder.id) {
          this.onFolderChange('all')
        } else {
          await this.loadFiles()
        }
        await this.loadFolders()
        this.toast('Folder deleted. Its files are back in All files.')
      } catch (error) {
        this.toast('Could not delete the folder.', 'danger')
      }
    },

    async setFolders({ fileId, folderIds }) {
      if (!this.canManageFolders) return
      const ids = folderIds.map(Number)
      try {
        await Api.put(`file/${fileId}/folders`, { folder_ids: ids })
        const entry = this.files.find((f) => (f.file || f).id === fileId)
        if (entry) this.$set(entry.file || entry, 'folder_ids', ids)
        await this.loadFolders()
        const open = /^\d+$/.test(this.selectedFolder)
        if (open && !ids.includes(Number(this.selectedFolder))) {
          await this.loadFiles()
        }
      } catch (error) {
        this.toast('Could not update the folders.', 'danger')
      }
    },
  },

  created() {
    // Every row seen so far, so a selection that spans pages can still be filed.
    this.rowCache = {}
    const firstLoad = [this.loadFolders(), this.loadProjects()]
    if (!this.onProjectSide(this.selectedFolder)) {
      firstLoad.push(this.loadCounts(), this.loadFiles())
    }
    // Each loader handles its own errors, so a failed one still lets the page open.
    Promise.all(firstLoad)
      .catch(() => {})
      .then(() => {
        this.ready = true
      })
  },

  watch: {
    rows(rows) {
      if (!rows) return
      rows.forEach((row) => {
        this.rowCache[row.id] = row
      })
    },

    selectedFolder() {
      if (this.onProjectSide(this.selectedFolder)) return
      this.loadFiles()
      this.loadCounts()
    },

    selectedType() {
      this.loadFiles()
    },

    search() {
      this.loadFiles()
      this.loadCounts()
    },

    sort() {
      this.loadFiles()
    },

    currentPage() {
      this.loadFiles()
    },

    queryState(query) {
      this.$router.replace({ query }).catch(() => {})
    },
  },
}
</script>

<style scoped>
/* This site's columns have no side padding, so the row's -15px pull-out would
   push the cards to (or past) the screen edge; below lg the container's own
   padding is the only margin. */
@media (max-width: 991.98px) {
  .files-row {
    margin-right: 0;
    margin-left: 0;
  }
}

/* Below lg: the button that opens the sidebar's folders and projects. */
.view-switcher {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 56px;
  padding: 8px 16px;
  background-color: #ffffff;
  border: 1px solid #e1e5ea;
  border-radius: 14px;
  color: #111827;
  font-family: inherit;
  text-align: left;
}

.view-switcher:hover,
.view-switcher[aria-expanded='true'] {
  border-color: #006cff;
}

.view-switcher-text {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.view-switcher-label {
  overflow: hidden;
  font-size: 14px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.view-switcher-hint {
  color: #6b7280;
  font-size: 12px;
}

.view-switcher-count {
  color: #6b7280;
  font-size: 14px;
}

.files-card {
  background-color: #ffffff;
  border: 1px solid #e1e5ea;
  border-radius: 14px;
}

.crumb-link {
  display: inline-flex;
  align-items: center;
  margin-bottom: 4px;
  padding: 0;
  background: transparent;
  border: 0;
  color: #006cff;
  font-size: 14px;
}

.crumb-link svg {
  margin-right: 4px;
}

.crumb-link:hover {
  text-decoration: underline;
}

.project-view-title {
  margin-bottom: 0;
  color: #111827;
  font-weight: 700;
}

.project-view-note {
  margin: 4px 0 0;
  color: #6b7280;
  font-size: 14px;
}

.project-view-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

/* Plain link styled as a small outline button: the global .btn is 56px tall. */
.project-view-link {
  flex-shrink: 0;
  padding: 6px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  color: #374151;
  font-size: 14px;
  font-weight: 600;
}

.project-view-link:hover {
  border-color: #006cff;
  color: #006cff;
  text-decoration: none;
}
</style>
